import is from '@sindresorhus/is';
import { parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';

import type { JobProfilesQuickFilter, JobProfilesTypeFilter } from '../api/job-profile-management-filter-types';

export type {
    JobProfilesQuickFilter,
    JobProfilesReviewerAssignmentFilter,
    JobProfilesTypeFilter,
} from '../api/job-profile-management-filter-types';

const QUICK_FILTER_PARAM = 'quickFilter';
const REVIEWER_PARAM = 'reviewerId';
const SEARCH_PARAM = 'search';
const TYPE_PARAM = 'type';

export const JOB_PROFILES_FILTER_SEARCH_PARAMS = [
    QUICK_FILTER_PARAM,
    REVIEWER_PARAM,
    SEARCH_PARAM,
    TYPE_PARAM,
] as const;

const DEFAULT_TYPE_FILTERS: readonly JobProfilesTypeFilter[] = ['role', 'level'];

const queryStateParsers = {
    quickFilter: parseAsStringLiteral(['for-you', 'unassigned'] as const),
    reviewerId: parseAsString,
    search: parseAsString.withDefault(''),
    type: parseAsStringLiteral(['level', 'role'] as const),
};

const queryStateOptions = { history: 'replace' } as const;

export type JobProfilesFilters = {
    readonly quickFilter: JobProfilesQuickFilter | undefined;
    readonly reviewerId: string | undefined;
    readonly searchTerm: string;
    readonly setQuickFilter: (quickFilter: JobProfilesQuickFilter | undefined) => void;
    readonly setReviewerId: (reviewerId: string | undefined) => void;
    readonly setSearchTerm: (searchTerm: string) => void;
    readonly setType: (type: JobProfilesTypeFilter | undefined) => void;
    readonly type: JobProfilesTypeFilter | undefined;
    readonly typeFilters: readonly JobProfilesTypeFilter[];
};

/**
 * Reads/writes the job-profiles list filters from URL search params so the tenant-wide view is
 * deep-linkable. No pagination param: the table uses infinite scroll.
 *
 * `type` is the user's explicit selection (drives the dropdown). `typeFilters` is the effective
 * server-side constraint: this page only ever lists role and level profiles, so when no type is
 * selected we default to both rather than letting the search return clusters and families.
 */
export const useJobProfilesFilters = (): JobProfilesFilters => {
    const [params, setParams] = useQueryStates(queryStateParsers, queryStateOptions);

    const reviewerId = is.nonEmptyString(params.reviewerId) ? params.reviewerId : undefined;
    const searchTerm = params.search;
    const type = params.type ?? undefined;
    const typeFilters = useMemo(() => (is.undefined(type) ? DEFAULT_TYPE_FILTERS : [type]), [type]);
    const quickFilter = params.quickFilter ?? undefined;

    const setSearchTerm = useCallback(
        (next: string): void => {
            const trimmed = next.trim();
            void setParams({ search: is.nonEmptyString(trimmed) ? trimmed : null });
        },
        [setParams],
    );

    const setReviewerId = useCallback(
        (next: string | undefined): void => {
            void setParams(is.nonEmptyString(next) ? { quickFilter: null, reviewerId: next } : { reviewerId: null });
        },
        [setParams],
    );

    const setType = useCallback(
        (next: JobProfilesTypeFilter | undefined): void => {
            void setParams(is.nonEmptyString(next) ? { quickFilter: null, type: next } : { type: null });
        },
        [setParams],
    );

    const setQuickFilter = useCallback(
        (next: JobProfilesQuickFilter | undefined): void => {
            void setParams(
                is.nonEmptyString(next) ? { quickFilter: next, reviewerId: null, type: null } : { quickFilter: null },
            );
        },
        [setParams],
    );

    return {
        quickFilter,
        reviewerId,
        searchTerm,
        setQuickFilter,
        setReviewerId,
        setSearchTerm,
        setType,
        type,
        typeFilters,
    };
};
